/**
 * Popover — nicht-modales Dialog-Panel unter einem Trigger (Redesign S2 Phase
 * 2a, Filter-Chips). Für Panels mit eigenen Controls (Checkbox-Listen,
 * Combobox, Select); `ActionMenu` bleibt für reine Aktionslisten (role=menu).
 *
 * Mechanik wie ActionMenu (früher auch DownloadDropdown) (patterns/react.md §
 * close-menu-before-action): Esc + Outside-Click schließen. Esc gibt den Fokus
 * an den Trigger zurück (patterns/react-modal.md — imperativ beim Schließen,
 * nicht per Effect im Child). Outside-Click lässt den Fokus dort, wo der User
 * hingeklickt hat.
 *
 * Positionierung `fixed` an den Trigger-Koordinaten statt `absolute`: der
 * Trigger sitzt auf Mobile in einem `overflow-x-auto`-Scroller, der ein
 * absolutes Panel abschneiden würde (overflow-x:auto erzwingt overflow-y:auto).
 * `fixed` entkommt dem Clipping, das Panel bleibt aber im DOM direkt hinter
 * dem Trigger → Tab-Reihenfolge Trigger → Panel-Inhalt → nächster Chip, ohne
 * Portal und ohne Fokus-Falle. Position folgt Scroll (capture, auch innere
 * Scroller) und Resize.
 *
 * Bewusst OHNE max-height/overflow: eingebettete Dropdowns (FilterCombobox-
 * Liste, `absolute`) würden sonst im Panel abgeschnitten. Lange Inhalte
 * scrollen in sich (ProductCategoryTree `max-h-[60vh]`).
 *
 * Eingebettete Controls, die Esc selbst verbrauchen (offene Combobox-Liste,
 * Suchfeld mit Text), rufen `e.stopPropagation()` — dann erreicht das Event den
 * document-Listener hier nicht, und erst das nächste Esc schließt das Panel.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';

const GAP = 6;
const EDGE = 8;

interface PopoverProps {
  open: boolean;
  /** `reason` = 'escape' | 'outside' — Esc gibt den Fokus an den Trigger zurück. */
  onClose: (reason: 'escape' | 'outside') => void;
  /** Trigger-Button: Positionsanker + Fokus-Ziel bei Esc. */
  anchorRef: RefObject<HTMLElement | null>;
  /**
   * Elemente, deren Klick NICHT als „außerhalb" zählt (Trigger-Gruppe inkl.
   * eines ×-Buttons). Default: nur der Anker.
   */
  insideRef?: RefObject<HTMLElement | null>;
  /** Accessible Name des Dialogs. */
  label: string;
  id?: string;
  children: ReactNode;
  className?: string;
}

export function Popover({
  open,
  onClose,
  anchorRef,
  insideRef,
  label,
  id,
  children,
  className,
}: PopoverProps): React.JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const place = useCallback(() => {
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;
    const r = anchor.getBoundingClientRect();
    const width = panel.offsetWidth;
    const left = Math.max(EDGE, Math.min(r.left, window.innerWidth - width - EDGE));
    setPos({ top: r.bottom + GAP, left });
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose('escape');
      anchorRef.current?.focus();
    }
    function onDown(e: MouseEvent): void {
      const t = e.target as Node;
      if (panelRef.current?.contains(t)) return;
      const inside = insideRef?.current ?? anchorRef.current;
      if (inside?.contains(t)) return;
      onClose('outside');
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, onClose, anchorRef, insideRef, place]);

  if (!open) return null;
  return (
    <div
      ref={panelRef}
      id={id}
      role="dialog"
      aria-label={label}
      style={
        pos
          ? { top: pos.top, left: pos.left }
          : { top: 0, left: 0, visibility: 'hidden' }
      }
      className={`fixed z-30 w-[min(22rem,calc(100vw-1rem))] rounded-2xl border border-ink-100 bg-white p-4 shadow-pop gf-pop-in ${className ?? ''}`}
    >
      {children}
    </div>
  );
}
